import { forwardRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { register as registerApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Layout } from "@/components/Layout";

const schema = z
  .object({
    email: z.string().email("Adresse email invalide"),
    password: z.string().min(12, "Mot de passe : au moins 12 caractères"),
    password_confirm: z.string(),
    cgu_accepted: z.boolean().refine((v) => v === true, {
      message: "Vous devez accepter les conditions générales",
    }),
    marketing_emails_opt_in: z.boolean(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: "Les mots de passe ne correspondent pas",
    path: ["password_confirm"],
  });

type FormData = z.infer<typeof schema>;

const CGU_VERSION = "1.0";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { marketing_emails_opt_in: false },
  });

  const mutation = useMutation({
    mutationFn: registerApi,
    onSuccess: () => setSuccess(true),
    onError: (err: ApiError) => {
      if (err.code === "PASSWORD_COMPROMISED") {
        setFormError(
          "Ce mot de passe apparaît dans des fuites de données connues. Choisissez-en un autre.",
        );
      } else {
        setFormError("Une erreur est survenue, veuillez réessayer.");
      }
    },
  });

  const onSubmit = (data: FormData) => {
    setFormError(null);
    mutation.mutate({
      email: data.email,
      password: data.password,
      cgu_version: CGU_VERSION,
      marketing_emails_opt_in: data.marketing_emails_opt_in,
    });
  };

  if (success) {
    return (
      <Layout>
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <div className="lg-card p-8">
          <h1 className="text-2xl font-bold">Vérifiez votre email</h1>
          <p className="mt-4 text-gray-700">
            Nous vous avons envoyé un lien de confirmation. Cliquez dessus pour activer
            votre compte.
          </p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="mt-6 text-sm text-blue-900 underline"
          >
            Aller à la page de connexion
          </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-md px-6 py-12">
        <div className="lg-card p-8">
        <h1 className="text-2xl font-bold">Créer un compte</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register("email")}
          />
          <Field
            label="Mot de passe (12 caractères min.)"
            type="password"
            autoComplete="new-password"
            error={errors.password?.message}
            {...register("password")}
          />
          <Field
            label="Confirmer le mot de passe"
            type="password"
            autoComplete="new-password"
            error={errors.password_confirm?.message}
            {...register("password_confirm")}
          />

          <Checkbox
            label={
              <>
                J'accepte les{" "}
                <Link to="/cgu" className="text-blue-900 underline dark:text-blue-300">
                  CGU
                </Link>{" "}
                et la{" "}
                <Link to="/confidentialite" className="text-blue-900 underline dark:text-blue-300">
                  politique de confidentialité
                </Link>
              </>
            }
            error={errors.cgu_accepted?.message}
            {...register("cgu_accepted")}
          />
          <Checkbox label="Je souhaite recevoir des emails de conseils (optionnel)" {...register("marketing_emails_opt_in")} />

          {formError && (
            <p role="alert" className="text-sm text-red-700">
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {mutation.isPending ? "Création…" : "Créer mon compte"}
          </button>
        </form>

        <p className="mt-6 text-sm text-gray-600">
          Déjà un compte ?{" "}
          <Link to="/login" className="text-blue-900 underline">
            Se connecter
          </Link>
        </p>
        </div>
      </div>
    </Layout>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
};

// forwardRef indispensable : sans ça, le ref renvoyé par RHF register() ne
// remonte pas jusqu'au <input> et les valeurs ne sont jamais captées.
const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div>
      <label htmlFor={inputId} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-err` : undefined}
        {...rest}
      />
      {error && (
        <p id={`${inputId}-err`} role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
});

type CheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: React.ReactNode;
  error?: string;
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, error, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div>
      <label className="flex items-start gap-2 text-sm" htmlFor={inputId}>
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className="mt-1"
          aria-invalid={!!error}
          {...rest}
        />
        <span>{label}</span>
      </label>
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
});
